import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Heart, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * /join/:code — public landing page when someone clicks a caregiver's referral link.
 * Looks up the referrer's name, shows a warm "Sarah invited you" intro, and
 * passes the code through to the beta signup form.
 */
export default function JoinByInvite() {
    const { code } = useParams();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [state, setState] = useState({ loading: true, error: null, referrer: null });

    useEffect(() => {
        // Stash the code so the BetaSignup page can read it on the next step.
        try { sessionStorage.setItem("mlcn_referrer_code", code || ""); } catch { /* ignore */ }
        api.get(`/auth/invite-lookup/${encodeURIComponent(code || "")}`)
            .then(r => setState({ loading: false, error: null, referrer: r.data }))
            .catch(() => setState({
                loading: false,
                error: "We couldn't find that invite. Want to sign up anyway?",
                referrer: null,
            }));
    }, [code]);

    // If they're already signed in, just send them to the dashboard.
    useEffect(() => {
        if (user && user !== false) {
            toast.info("You're already signed in.");
            navigate("/dashboard");
        }
    }, [user, navigate]);

    return (
        <div className="min-h-screen bg-cream flex items-center justify-center p-6">
            <div className="max-w-lg w-full">
                <div className="flex items-center justify-center gap-2 text-teal-dark mb-6">
                    <Heart strokeWidth={1.6} size={20} />
                    <span className="text-sm uppercase tracking-wider font-semibold">Memory Lane Care Navigator</span>
                </div>

                {state.loading && (
                    <div className="ml-card p-8 text-center text-[#4C5551]">
                        <Loader2 className="mx-auto animate-spin mb-3 text-teal" />
                        Checking your invite…
                    </div>
                )}

                {!state.loading && state.referrer && (
                    <div className="ml-card p-8 md:p-10 text-center" data-testid="invite-welcome">
                        <p className="text-sm uppercase tracking-wider font-semibold text-teal mb-2">You&apos;ve been invited</p>
                        <h1 className="text-3xl md:text-4xl font-heading font-semibold tracking-tight text-[#12776F]">
                            {state.referrer.referrer_name} thought Memory Lane could help you too.
                        </h1>
                        <p className="mt-4 text-lg text-[#4C5551] leading-relaxed">
                            Memory Lane is a GPS for dementia caregivers — personalized roadmaps,
                            scripts for the hardest conversations, and an AI companion who remembers your story.
                        </p>
                        <p className="mt-3 text-base text-[#4C5551]">
                            It&apos;s free during our private beta. Three minutes to set up.
                        </p>
                        <Link
                            to="/beta"
                            data-testid="invite-accept"
                            className="mt-6 inline-flex items-center gap-2 bg-teal text-white hover:bg-teal-dark rounded-2xl px-6 py-3 font-medium"
                        >
                            Request my spot <ArrowRight size={16} strokeWidth={1.8} />
                        </Link>
                        <p className="mt-4 text-xs text-[#6E7A74]">
                            Invite code: <code className="bg-cream-dark/60 px-1.5 py-0.5 rounded">{state.referrer.code}</code>
                        </p>
                    </div>
                )}

                {!state.loading && state.error && (
                    <div className="ml-card p-8 text-center" data-testid="invite-error">
                        <h1 className="text-2xl font-heading font-medium text-[#12776F]">
                            Hmm, that invite didn&apos;t work.
                        </h1>
                        <p className="mt-3 text-[#4C5551]">{state.error}</p>
                        <Link to="/beta" className="ml-btn-primary mt-5 inline-flex">
                            Try Memory Lane instead <ArrowRight size={16} strokeWidth={1.8} />
                        </Link>
                    </div>
                )}
            </div>
        </div>
    );
}
